import { useCallback, useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Web NFC type definitions
// ---------------------------------------------------------------------------

interface NDEFReadingEvent extends Event {
  serialNumber?: string;
  message?: unknown;
}

interface NDEFReaderInstance extends EventTarget {
  scan: () => Promise<void>;
  abort?: () => void;
  addEventListener(
    type: "reading",
    listener: (event: NDEFReadingEvent) => void,
  ): void;
  addEventListener(
    type: "readingerror",
    listener: (event: Event) => void,
  ): void;
}

interface NDEFReaderConstructor {
  new (): NDEFReaderInstance;
}

declare global {
  interface Window {
    NDEFReader?: NDEFReaderConstructor;
  }
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ScannedCard {
  /** Cleaned UID – colons removed, uppercased (e.g. "AABBCCDD") */
  serialNumber: string;
  /** Original serial number as reported by the reader (e.g. "aa:bb:cc:dd") */
  rawSerialNumber: string;
  /** Timestamp of the scan */
  scannedAt: Date;
}

export type NfcScanStatus =
  | "idle"
  | "scanning"
  | "success"
  | "unsupported"
  | "error"
  | "timeout";

export interface UseNfcScannerOptions {
  /** Timeout in ms before the scan is considered timed-out. Default: 30 000 */
  timeoutMs?: number;
}

export interface UseNfcScannerReturn {
  status: NfcScanStatus;
  scannedCard: ScannedCard | null;
  errorMessage: string;
  isNfcSupported: boolean;
  startScan: () => void;
  stopScan: () => void;
  resetScan: () => void;
}

export const isValidNfcUid = (uid: string): boolean => {
  if (!uid || typeof uid !== "string") return false;
  const clean = uid.replace(/:/g, "").trim();
  const isHex = /^[0-9A-Fa-f]+$/.test(clean);
  return isHex && clean.length >= 4 && clean.length <= 32;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const DEFAULT_TIMEOUT_MS = 30_000;

export const useNfcScanner = (
  options?: UseNfcScannerOptions,
): UseNfcScannerReturn => {
  const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const [status, setStatus] = useState<NfcScanStatus>("idle");
  const [scannedCard, setScannedCard] = useState<ScannedCard | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const isNfcSupported =
    typeof window !== "undefined" && "NDEFReader" in window;

  // Refs for cleanup & duplicate-scan prevention
  const ndefControllerRef = useRef<NDEFReaderInstance | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasScannedRef = useRef(false);

  // ------ helpers ------

  const clearScanTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const abortReader = useCallback(() => {
    if (
      ndefControllerRef.current &&
      typeof ndefControllerRef.current.abort === "function"
    ) {
      ndefControllerRef.current.abort();
    }
    ndefControllerRef.current = null;
  }, []);

  // ------ public API ------

  const stopScan = useCallback(() => {
    clearScanTimeout();
    abortReader();
  }, [clearScanTimeout, abortReader]);

  const resetScan = useCallback(() => {
    stopScan();
    hasScannedRef.current = false;
    setStatus("idle");
    setScannedCard(null);
    setErrorMessage("");
  }, [stopScan]);

  const startScan = useCallback(() => {
    // Guard: browser support
    if (!isNfcSupported || !window.NDEFReader) {
      setStatus("unsupported");
      return;
    }

    // Reset previous state
    hasScannedRef.current = false;
    setScannedCard(null);
    setErrorMessage("");
    setStatus("scanning");

    const initScan = async () => {
      try {
        const NDEFReaderClass = window.NDEFReader;
        if (!NDEFReaderClass) {
          setStatus("unsupported");
          return;
        }

        const reader = new NDEFReaderClass();
        ndefControllerRef.current = reader;

        await reader.scan();

        // Start timeout timer
        clearScanTimeout();
        timeoutRef.current = setTimeout(() => {
          // Only timeout if we haven't already scanned
          if (!hasScannedRef.current) {
            abortReader();
            setStatus("timeout");
            setErrorMessage(
              "Scan timed out. No RFID tag was detected. Please try again.",
            );
          }
        }, timeoutMs);

        // Listen: successful read
        reader.addEventListener("reading", (event: NDEFReadingEvent) => {
          // Prevent duplicate reads
          if (hasScannedRef.current) return;

          const serialNumber = event.serialNumber;
          if (serialNumber) {
            hasScannedRef.current = true;
            clearScanTimeout();

            const cleanSerial = serialNumber.replace(/:/g, "").toUpperCase();

            if (!isValidNfcUid(cleanSerial)) {
              setStatus("error");
              setErrorMessage("Invalid RFID tag UID format detected.");
              return;
            }

            setScannedCard({
              serialNumber: cleanSerial,
              rawSerialNumber: serialNumber,
              scannedAt: new Date(),
            });
            setStatus("success");
          }
        });

        // Listen: read error
        reader.addEventListener("readingerror", () => {
          setStatus("error");
          setErrorMessage("Failed to read RFID tag. Please tap card again.");
        });
      } catch (err: unknown) {
        console.error("NFC Scan Error:", err);
        const error = err as Error;
        setStatus("error");
        setErrorMessage(
          error.message ||
            "Unable to access NFC scanner. Please check device permissions.",
        );
      }
    };

    initScan();
  }, [isNfcSupported, timeoutMs, clearScanTimeout, abortReader]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearScanTimeout();
      abortReader();
    };
  }, [clearScanTimeout, abortReader]);

  return {
    status,
    scannedCard,
    errorMessage,
    isNfcSupported,
    startScan,
    stopScan,
    resetScan,
  };
};
